import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, httpPublic } from "@/lib/api";
import { Search, Flower, Calendar, Sparkles, Lightbulb, X, Loader2, TreeDeciduous, ArrowUpRight, Bell } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useCountryFilter } from "@/lib/useCountryFilter";
import CountryFilterBanner from "@/components/CountryFilterBanner";
import { paletteFor } from "@/lib/palette";
import MemorialBadges from "@/components/MemorialBadges";
import { silhouetteOnError } from "@/lib/silhouette";
import InitialsAvatar from "@/components/InitialsAvatar";

const INTRO_QUOTE = `"Celebrate a Life" is a permanent digital memorial that allows every person, regardless of fame or status, to have their life remembered, celebrated, and preserved for future generations.

Unlike a traditional obituary, Celebrate a Life is intended to become a living historical record that grows over time as family members, friends, and communities contribute memories, photographs, stories, videos, documents, and historical context.

The objective is to ensure that every life has value and every story has a permanent place in history.`;

const PRINCIPLES = [
  "Every person deserves to be remembered.",
  "Memories should improve over time rather than disappear.",
  "Family members should collaboratively preserve history.",
  "Communities should celebrate lives, not simply announce deaths.",
  "The platform should eventually become one of the world's largest searchable historical archives of people.",
];

const CELEBRATE_ILLUSTRATION = "https://customer-assets-39nsmqrw.emergentagent.net/job_kindness-hub-35/artifacts/umeecto8_memorial-grahic.jpg";

export default function Celebrate() {
  const [recent, setRecent] = useState([]);
  const [anniv, setAnniv] = useState([]);
  const [discovery, setDiscovery] = useState([]);
  const [discoveryFacets, setDiscoveryFacets] = useState({ sources: [], countries: [] });
  const [discoveryFilter, setDiscoveryFilter] = useState({ source: "", country: "" });
  const [q, setQ] = useState("");
  const [nomOpen, setNomOpen] = useState(false);
  // "Recent memorials" section — the user wants a random-order feed by
  // default, filterable by place + gender.
  const [memorialFilter, setMemorialFilter] = useState({ gender: "", location: "" });
  const [memorialSort, setMemorialSort] = useState("random"); // random | recent | name
  const recentF = useCountryFilter(recent, ["birth_place", "death_place", "nationality"]);
  const annivF = useCountryFilter(anniv, ["birth_place", "death_place", "nationality"]);

  useEffect(() => {
    httpPublic.get("/memorials/today-anniversaries").then((r) => setAnniv(r.data.items || [])).catch(() => {});
    httpPublic.get("/obituaries/facets").then((r) => setDiscoveryFacets(r.data || { sources: [], countries: [] })).catch(() => {});
  }, []);

  useEffect(() => {
    httpPublic.get("/obituaries", {
      params: {
        source: discoveryFilter.source || undefined,
        country: discoveryFilter.country || undefined,
        limit: 12,
      },
    }).then((r) => setDiscovery(r.data.items || [])).catch(() => setDiscovery([]));
  }, [discoveryFilter.source, discoveryFilter.country]);

  useEffect(() => {
    httpPublic.get("/memorials", {
      params: {
        sort: memorialSort,
        gender: memorialFilter.gender || undefined,
        location: memorialFilter.location || undefined,
        limit: 12,
      },
    }).then((r) => setRecent(r.data.items || [])).catch(() => {});
  }, [memorialSort, memorialFilter.gender, memorialFilter.location]);

  return (
    <div data-testid="celebrate-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
      {/* Marketing / product overview link — top of page */}
      <Link
        to="/family-and-memorials"
        className="group flex flex-wrap items-center justify-between gap-3 rounded-2xl border-2 border-dashed px-5 py-3 mb-6 hover:bg-white transition-colors"
        style={{ borderColor: "#B23A4E44", background: "#FDFBF7" }}
        data-testid="celebrate-marketing-link"
      >
        <div className="flex items-center gap-3">
          <span className="text-lg">🕊</span>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em]" style={{ color: "#B23A4E" }}>
              New here?
            </div>
            <div className="text-sm font-medium" style={{ color: "#2D2A26" }}>
              Family Trees &amp; Eternal Memorials &mdash; <span className="opacity-70">celebrate a life well lived</span>
            </div>
          </div>
        </div>
        <span
          className="text-sm inline-flex items-center gap-1.5 font-medium group-hover:gap-2 transition-all"
          style={{ color: "#B23A4E" }}
        >
          Read the full story <ArrowUpRight className="w-4 h-4" />
        </span>
      </Link>

      {/* NOTE: Removed the green "Every Family Has a Story" teal banner
          and the "Social Pulse — Honoring Memories" illustration board
          per product request (2026-08-22). The page now leads straight
          into the Memorial Directory experience with existing memorial
          photos surfaced up top. */}

      <div className="grid md:grid-cols-3 gap-10 items-start">
        <div className="md:col-span-2">
          <div className="overline mb-3" style={{ color: "var(--wofy-primary)" }}>CELEBRATE A LIFE · ETERNAL MEMORIALS</div>
          <h1 className="font-display text-5xl md:text-6xl leading-[1.05]">Eternal Memorials for every life.</h1>

          {/* Actions — "Add a memorial" and "Announce a Death" grouped in one box */}
          <div className="mt-8 flex flex-wrap gap-3 items-start">
            <div
              className="rounded-2xl border p-3 flex flex-col gap-2 shadow-sm"
              style={{ borderColor: "var(--wofy-border)", background: "rgba(255,255,255,0.6)" }}
              data-testid="celebrate-add-box"
            >
              <Link to="/celebrate/submit" data-testid="celebrate-cta-add" className="wofy-btn-primary justify-center">
                <Flower className="w-4 h-4" /> Create an Eternal Memorial
              </Link>
              <Link
                to="/celebrate/submit?type=death_announcement"
                data-testid="celebrate-cta-announce-death"
                className="wofy-btn-ghost justify-center text-xs"
              >
                <Bell className="w-3.5 h-3.5" /> Announce a Death
              </Link>
            </div>
            <Link to="/celebrate/link-existing" data-testid="celebrate-cta-link" className="wofy-btn-ghost">
              Link an existing obituary
            </Link>
            <Link to="/obituaries" data-testid="celebrate-cta-obituaries" className="wofy-btn-ghost">
              Obituaries archive
            </Link>
            <Link to="/celebrate/browse" data-testid="celebrate-cta-browse" className="wofy-btn-ghost">
              Browse all memorials
            </Link>
            <button
              type="button"
              data-testid="celebrate-cta-nominate"
              onClick={() => setNomOpen(true)}
              className="wofy-btn-ghost"
            >
              <Lightbulb className="w-4 h-4" /> Suggest a memorial
            </button>
          </div>

          {/* Existing memorials preview — real photos with links so the
              directory feels populated the moment a visitor lands. The
              illustration board that used to live here was removed
              because it competed with the actual memorial content. */}
          <section className="mt-10" data-testid="celebrate-memorial-preview-grid">
            <div className="flex items-end justify-between mb-4">
              <div>
                <div className="overline" style={{ color: "var(--wofy-primary)" }}>MEMORIAL DIRECTORY</div>
                <h2 className="font-display text-2xl md:text-3xl mt-1">Recently remembered</h2>
              </div>
              <Link
                to="/celebrate/browse"
                className="text-sm font-semibold inline-flex items-center gap-1 hover:gap-2 transition-all"
                style={{ color: "var(--wofy-primary)" }}
                data-testid="celebrate-preview-browse-all"
              >
                Browse all <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {(recentF || []).slice(0, 12).map((m) => {
                // ``memorial_card_url`` was a Nano-Banana + PIL composite
                // that baked tofu boxes into any Malayalam/Tamil/Hindi
                // name (DejaVu Serif has no Indic glyph coverage). Skip
                // it here — prefer the AI-cleaned portrait or the raw
                // photo. Fall back to a colourful initials tile so
                // photo-less members feel welcoming, not empty.
                const isInitials = m.portrait_style === "initials";
                const realImg = m.ai_portrait || (isInitials ? null : m.portrait);
                return (
                  <Link
                    key={m.id}
                    to={`/celebrate/${m.slug || m.id}`}
                    data-testid={`celebrate-preview-card-${m.id}`}
                    className="group block rounded-xl overflow-hidden border transition-transform hover:-translate-y-0.5 hover:shadow-md"
                    style={{ borderColor: "var(--wofy-border)", background: "#FFF8EC" }}
                  >
                    <div className="aspect-square w-full overflow-hidden bg-[color:var(--wofy-surface)]">
                      {realImg ? (
                        <img
                          src={realImg}
                          alt={m.full_name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                          onError={silhouetteOnError}
                        />
                      ) : (
                        <InitialsAvatar
                          name={m.full_name}
                          gender={m.gender}
                          fill
                          square
                          className="w-full h-full !rounded-none"
                        />
                      )}
                    </div>
                    <div className="px-2.5 py-2">
                      <div className="text-xs font-semibold truncate" title={m.full_name} style={{ color: "var(--wofy-text)" }}>
                        {m.full_name}
                      </div>
                      {(m.death_date || m.location?.city || m.location?.state) && (
                        <div className="text-[10px] font-mono opacity-70 truncate">
                          {[m.death_date, m.location?.city || m.location?.state].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>

          <blockquote className="mt-8 pl-6 border-l-4 whitespace-pre-line text-base leading-relaxed" style={{ borderColor: "var(--wofy-primary)", color: "var(--wofy-text-soft)" }}>
            {INTRO_QUOTE}
          </blockquote>

          {/* Search */}
          <form className="mt-8 flex gap-2" onSubmit={(e) => { e.preventDefault(); window.location.href = `/celebrate/browse?q=${encodeURIComponent(q)}`; }}>
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
              <input
                data-testid="celebrate-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, location, occupation…"
                className="w-full rounded-full border pl-10 pr-4 py-3 text-sm bg-transparent"
                style={{ borderColor: "var(--wofy-border)" }}
              />
            </div>
            <button className="wofy-btn-primary">Search</button>
          </form>
        </div>

        <aside className="wofy-card">
          <div className="overline mb-3" style={{ color: "var(--wofy-primary)" }}>OUR PRINCIPLES</div>
          <ul className="space-y-3">
            {PRINCIPLES.map((p, i) => (
              <li key={i} className="flex gap-3 text-sm leading-relaxed">
                <Sparkles className="w-4 h-4 shrink-0 mt-1" style={{ color: "var(--wofy-primary)" }} />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </aside>
      </div>

      {/* Cemeteries teaser — quick jump into the sacred-grounds directory
          from the Memorials landing. Requested on MyFamilyTree.care. */}
      <section className="mt-14" data-testid="celebrate-cemeteries-teaser">
        <Link
          to="/cemeteries"
          className="group grid md:grid-cols-[minmax(0,1fr)_auto] items-center gap-6 rounded-3xl border p-6 md:p-8 hover:shadow-md transition-shadow"
          style={{ borderColor: "var(--wofy-border)", background: "linear-gradient(135deg, var(--wofy-surface) 0%, #ffffff 60%, rgba(178,93,56,0.08) 100%)" }}
        >
          <div>
            <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>SACRED GROUNDS · 55 LISTINGS</div>
            <h3 className="font-display text-2xl md:text-3xl mb-2">Cemeteries &amp; Burial Grounds</h3>
            <p className="text-sm md:text-base" style={{ color: "var(--wofy-text-soft)" }}>
              Christian cemeteries, Muslim <em>kabristans</em>, Hindu ghats, Parsi Towers of Silence, Jewish burial grounds and CWGC military cemeteries — searchable by city, country and faith. Link a memorial to its resting place, or find the ancestral ground your family has visited for generations.
            </p>
            <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold group-hover:gap-2 transition-all" style={{ color: "var(--wofy-primary)" }} data-testid="celebrate-cemeteries-link">
              Browse the Cemeteries Directory →
            </div>
          </div>
          <div className="hidden md:block text-6xl opacity-30 select-none" aria-hidden>🕯️</div>
        </Link>
      </section>

      {/* Today's anniversaries */}
      {annivF.length > 0 && (
        <section className="mt-16">
          <div className="flex items-center gap-3 mb-6">
            <Calendar className="w-5 h-5" style={{ color: "var(--wofy-primary)" }} />
            <h2 className="font-display text-3xl">Today we remember</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {annivF.map((m) => <MemorialCard key={m.id} m={m} />)}
          </div>
        </section>
      )}

      {/* Discovery Feed — newly imported obituaries from partner sources */}
      {(discovery.length > 0 || discoveryFilter.source || discoveryFilter.country) && (
        <section className="mt-16" data-testid="celebrate-discovery-section">
          <div className="mb-4">
            <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>NEWLY REMEMBERED</div>
            <div className="flex items-baseline gap-3 flex-wrap">
              <h2 className="font-display text-3xl">Recently imported obituaries</h2>
              <Link to="/obituaries" className="text-sm underline underline-offset-4" data-testid="discovery-see-all">See all in archive →</Link>
            </div>
            <p className="mt-1 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
              Curated from Malayalam-language obituary sources — every card opens an IndianFamilyTree memorial page with the person's photo and details, with a citation link back to the original.
            </p>

            {/* Compact filter chips — source + country */}
            {(discoveryFacets.sources.length > 0 || discoveryFacets.countries.length > 0) && (
              <div className="mt-4 flex items-center gap-3 flex-wrap" data-testid="discovery-filter-bar">
                {discoveryFacets.sources.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "var(--wofy-text-soft)" }}>Source</span>
                    <DiscoveryChip
                      active={!discoveryFilter.source}
                      onClick={() => setDiscoveryFilter((f) => ({ ...f, source: "" }))}
                      testid="discovery-source-all"
                    >All</DiscoveryChip>
                    {discoveryFacets.sources.map((s) => (
                      <DiscoveryChip
                        key={s.source}
                        active={discoveryFilter.source === s.source}
                        onClick={() => setDiscoveryFilter((f) => ({ ...f, source: f.source === s.source ? "" : s.source }))}
                        testid={`discovery-source-${s.source}`}
                      >{s.attribution || s.source} <span className="opacity-60">({s.count})</span></DiscoveryChip>
                    ))}
                  </div>
                )}
                {discoveryFacets.countries.length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: "var(--wofy-text-soft)" }}>Region</span>
                    <DiscoveryChip
                      active={!discoveryFilter.country}
                      onClick={() => setDiscoveryFilter((f) => ({ ...f, country: "" }))}
                      testid="discovery-country-all"
                    >Everywhere</DiscoveryChip>
                    {discoveryFacets.countries.slice(0, 6).map((c) => (
                      <DiscoveryChip
                        key={c.country || "unknown"}
                        active={discoveryFilter.country === c.country}
                        onClick={() => setDiscoveryFilter((f) => ({ ...f, country: f.country === c.country ? "" : c.country }))}
                        testid={`discovery-country-${(c.country || 'unknown').toLowerCase()}`}
                      >{c.country || "Unknown"} <span className="opacity-60">({c.count})</span></DiscoveryChip>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {discovery.length === 0 ? (
            <div className="wofy-card text-center py-10 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
              No obituaries match those filters yet.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {discovery.map((m) => {
                const isInitialsPh = m.portrait_style === "initials";
                const realImg = m.ai_portrait || (isInitialsPh ? null : m.portrait);
                return (
                <Link
                  key={m.id}
                  to={`/celebrate/${m.slug || m.id}`}
                  className="wofy-card block hover:shadow-md transition-shadow overflow-hidden"
                  data-testid={`discovery-card-${m.id}`}
                >
                  {realImg ? (
                    <img
                      src={realImg}
                      alt={m.name_latin || m.full_name || "Memorial portrait"}
                      className="w-full aspect-square object-cover rounded-xl mb-3"
                      loading="lazy"
                      onError={silhouetteOnError}
                    />
                  ) : (
                    <InitialsAvatar
                      name={m.name_latin || m.full_name}
                      gender={m.gender}
                      fill
                      square
                      className="w-full aspect-square mb-3"
                    />
                  )}
                  <div className="font-display text-lg leading-tight truncate">
                    {m.name_latin || m.preferred_name || m.full_name}
                  </div>
                  {m.name_latin && m.full_name && m.name_latin !== m.full_name && (
                    <div className="text-xs opacity-70 mt-0.5 truncate">{m.full_name}</div>
                  )}
                  {(m.verification_badges?.length > 0) && (
                    <MemorialBadges
                      badges={m.verification_badges}
                      size="sm"
                      className="mt-1.5"
                      testidPrefix={`discovery-badge-${m.id}`}
                    />
                  )}
                  <div className="mt-2 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
                    {m.age_at_death ? `Age ${m.age_at_death}` : ""}
                    {m.age_at_death && m.location?.city ? " · " : ""}
                    {m.location?.city || ""}
                  </div>
                  {m.english_snippet && (
                    <p className="mt-2 text-sm leading-snug line-clamp-2" style={{ color: "var(--wofy-text-soft)" }}>
                      {m.english_snippet}
                    </p>
                  )}
                  <div className="mt-3 text-[10px] font-mono uppercase tracking-wide opacity-60">
                    via {m.source_attribution || m.source}
                  </div>
                </Link>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* Recent */}
      <section className="mt-16">
        <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
          <h2 className="font-display text-3xl">Recent memorials</h2>
          <div className="flex items-center gap-2 flex-wrap" data-testid="memorial-recent-controls">
            {/* Sort */}
            <select
              value={memorialSort}
              onChange={(e) => setMemorialSort(e.target.value)}
              data-testid="memorial-sort"
              className="rounded-full border px-3 py-1.5 text-xs bg-transparent"
              style={{ borderColor: "var(--wofy-border)" }}
              aria-label="Sort memorials"
            >
              <option value="random">Random</option>
              <option value="recent">Newest first</option>
              <option value="name">By name</option>
            </select>
            {/* Gender */}
            <select
              value={memorialFilter.gender}
              onChange={(e) => setMemorialFilter((f) => ({ ...f, gender: e.target.value }))}
              data-testid="memorial-gender-filter"
              className="rounded-full border px-3 py-1.5 text-xs bg-transparent"
              style={{ borderColor: "var(--wofy-border)" }}
              aria-label="Filter by gender"
            >
              <option value="">All</option>
              <option value="female">Women</option>
              <option value="male">Men</option>
              <option value="other">Other</option>
            </select>
            {/* Place */}
            <input
              value={memorialFilter.location}
              onChange={(e) => setMemorialFilter((f) => ({ ...f, location: e.target.value }))}
              placeholder="Filter by place…"
              data-testid="memorial-location-filter"
              className="rounded-full border px-3 py-1.5 text-xs bg-transparent w-40"
              style={{ borderColor: "var(--wofy-border)" }}
              aria-label="Filter memorials by place"
            />
            <Link to="/celebrate/browse" className="text-sm underline underline-offset-4">See all</Link>
          </div>
        </div>
        <CountryFilterBanner shown={recentF.length} total={recent.length} fallback={!!recentF.fallback} noun="memorials" />
        {recent.length === 0 ? (
          <div className="wofy-card text-center py-16">
            <div className="font-display text-2xl">Memorials are loading…</div>
            <p className="mt-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>Or be the first to celebrate someone's life.</p>
            <Link to="/celebrate/submit" className="mt-6 wofy-btn-primary inline-flex">Add a memorial</Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recentF.map((m) => <MemorialCard key={m.id} m={m} />)}
          </div>
        )}
      </section>

      {/* Browse index */}
      <section className="mt-16">
        <h2 className="font-display text-3xl mb-6">Browse the archive</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
          {[
            { label: "Recent Memorials", to: "/celebrate/browse?sort=recent" },
            { label: "Today's Anniversaries", to: "/celebrate/browse?anniversary=today" },
            { label: "Browse by Location", to: "/celebrate/browse?filter=location" },
            { label: "Browse by Year", to: "/celebrate/browse?filter=year" },
            { label: "Browse by Profession", to: "/celebrate/browse?filter=profession" },
            { label: "Veterans", to: "/celebrate/browse?profession=Military" },
            { label: "Educators", to: "/celebrate/browse?profession=Educator" },
            { label: "Healthcare heroes", to: "/celebrate/browse?profession=Healthcare" },
            { label: "Community leaders", to: "/celebrate/browse?profession=Volunteer%20%2F%20Community" },
            { label: "Public figures", to: "/celebrate/browse?filter=public_figures" },
            { label: "Children remembered", to: "/celebrate/browse?filter=children" },
            { label: "Forgotten history", to: "/celebrate/browse?filter=historical" },
          ].map((b) => (
            <Link key={b.label} to={b.to} className="wofy-card hover:shadow-md">
              {b.label} →
            </Link>
          ))}
        </div>
      </section>

      {nomOpen && <NominateModal onClose={() => setNomOpen(false)} />}
    </div>
  );
}

function NominateModal({ onClose }) {
  const { user } = useAuth();
  const [f, setF] = useState({
    full_name: "",
    wikipedia_url: "",
    reason: "",
    nominator_name: user?.name || "",
    nominator_email: user?.email || "",
  });
  const [busy, setBusy] = useState(false);

  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.full_name.trim()) return toast.error("Please enter the person's full name");
    if (!/^https?:\/\/[a-z]{2,3}\.wikipedia\.org\/wiki\//i.test(f.wikipedia_url.trim())) {
      return toast.error("Please paste a valid Wikipedia article URL");
    }
    if (!user && (!f.nominator_name.trim() || !f.nominator_email.trim())) {
      return toast.error("Please add your name and email (or sign in)");
    }
    setBusy(true);
    try {
      const { data } = await http.post("/memorials/nominations", f);
      if (data.duplicate) {
        toast.success("Thanks! This person is already in the review queue.");
      } else {
        toast.success("Thank you — your nomination is under review by our editorial team.");
      }
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Nomination failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(20,33,61,0.28)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
      data-testid="nominate-modal"
    >
      <div
        className="wofy-card w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>SUGGEST A MEMORIAL</div>
            <h3 className="font-display text-2xl">Nominate a life to celebrate</h3>
            <p className="mt-2 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
              Paste a Wikipedia link for a historical or notable person. Once approved, we auto-import
              their biography, portrait and sources so the world can remember them here.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="nominate-close"
            className="p-1 opacity-60 hover:opacity-100"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <label className="block text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
            Full name
            <input
              data-testid="nominate-name"
              value={f.full_name}
              onChange={(e) => upd("full_name", e.target.value)}
              placeholder="e.g. Rosa Parks"
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm bg-transparent font-sans normal-case tracking-normal"
              style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text)" }}
              required
            />
          </label>
          <label className="block text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
            Wikipedia URL
            <input
              data-testid="nominate-url"
              value={f.wikipedia_url}
              onChange={(e) => upd("wikipedia_url", e.target.value)}
              placeholder="https://en.wikipedia.org/wiki/Rosa_Parks"
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm bg-transparent font-sans normal-case tracking-normal"
              style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text)" }}
              required
            />
          </label>
          <label className="block text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
            Why this person? (optional)
            <textarea
              data-testid="nominate-reason"
              value={f.reason}
              onChange={(e) => upd("reason", e.target.value)}
              placeholder="Two lines on why they deserve a permanent tribute."
              rows={3}
              maxLength={800}
              className="mt-1 w-full rounded-lg border px-3 py-2 text-sm bg-transparent font-sans normal-case tracking-normal"
              style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text)" }}
            />
          </label>

          {!user && (
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
                Your name
                <input
                  data-testid="nominate-your-name"
                  value={f.nominator_name}
                  onChange={(e) => upd("nominator_name", e.target.value)}
                  className="mt-1 w-full rounded-lg border px-3 py-2 text-sm bg-transparent font-sans normal-case tracking-normal"
                  style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text)" }}
                  required
                />
              </label>
              <label className="block text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
                Your email
                <input
                  data-testid="nominate-your-email"
                  type="email"
                  value={f.nominator_email}
                  onChange={(e) => upd("nominator_email", e.target.value)}
                  className="mt-1 w-full rounded-lg border px-3 py-2 text-sm bg-transparent font-sans normal-case tracking-normal"
                  style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text)" }}
                  required
                />
              </label>
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="wofy-btn-ghost" data-testid="nominate-cancel">
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid="nominate-submit"
              className="wofy-btn-primary disabled:opacity-60"
            >
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : "Submit nomination"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function MemorialCard({ m }) {
  const years = [m.birth_date?.slice(0, 4), m.death_date?.slice(0, 4)].filter(Boolean).join("–");
  const palette = paletteFor(m.id || m.slug || m.full_name);
  // Skip ``memorial_card_url`` (tofu-baked) and legacy "initials"
  // portrait placeholders — fall back to a colourful initials tile so
  // photo-less members feel welcoming, not empty.
  const isInitials = m.portrait_style === "initials";
  const realImg = m.ai_portrait || (isInitials ? null : m.portrait);
  return (
    <Link
      to={`/celebrate/${m.slug || m.id}`}
      data-testid={`memorial-card-${m.id}`}
      className="rounded-2xl group overflow-hidden border block transition-all hover:-translate-y-0.5 hover:shadow-lg"
      style={{ borderColor: `${palette.accent}33`, background: `linear-gradient(135deg, ${palette.from} 0%, ${palette.to} 100%)` }}
    >
      <div className="aspect-[4/3] overflow-hidden">
        {realImg ? (
          <img
            src={realImg}
            alt={m.full_name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
            onError={silhouetteOnError}
          />
        ) : (
          <InitialsAvatar
            name={m.full_name}
            gender={m.gender}
            fill
            square
            className="w-full h-full !rounded-none"
          />
        )}
      </div>
      <div className="p-5">
        <div className="font-display text-xl leading-tight" style={{ color: palette.ink }}>{m.full_name}</div>
        {years && <div className="text-xs font-mono mt-1" style={{ color: palette.ink, opacity: 0.7 }}>{years}</div>}
        {(m.verification_badges?.length > 0) && (
          <MemorialBadges
            badges={m.verification_badges}
            size="sm"
            className="mt-2"
            testidPrefix={`memorial-card-badge-${m.id}`}
          />
        )}
        {m.occupation && <div className="text-sm mt-2" style={{ color: palette.ink, opacity: 0.8 }}>{m.occupation}</div>}
        {(m.birth_place || m.death_place) && <div className="mt-3 text-[11px] font-mono uppercase tracking-wider" style={{ color: palette.ink, opacity: 0.65 }}>{m.birth_place || m.death_place}</div>}
      </div>
    </Link>
  );
}

// Small chip used by the Discovery Feed filter bar. Compact styling so
// two rows of chips fit under the section header without dominating.
function DiscoveryChip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] transition-colors ${active ? "text-white" : "hover:bg-neutral-50"}`}
      style={active
        ? { background: "var(--wofy-primary)", borderColor: "var(--wofy-primary)" }
        : { borderColor: "var(--wofy-border)" }}
    >
      {children}
    </button>
  );
}
